import React from 'react'
import { Box, Stack, Typography } from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'

interface HolidaysSpotlightProps {
    message: string
    closeLabel: string
    onClose: () => void
    /** `up` points the caret at a host below the bubble; `down` at one above it. */
    caret: 'up' | 'down'
}

const CARET_SIZE = 7

/**
 * A bubble that points at the holidays entry on a first visit.
 *
 * It is positioned against its host, so it needs no measuring and cannot drift: the host carries
 * `position: relative`, and the bubble centres itself on it. `role="status"` announces it once to
 * a screen reader without stealing focus, which is what a tooltip nobody asked for should do.
 */
const HolidaysSpotlight: React.FC<HolidaysSpotlightProps> = ({ message, closeLabel, onClose, caret }) => {
    const pointsDown = caret === 'down'

    return (
        <Stack
            role="status"
            direction="row"
            spacing={0.5}
            sx={{
                position: 'absolute',
                insetInlineStart: '50%',
                // 10px clears the caret as well as the host.
                ...(pointsDown ? { bottom: 'calc(100% + 10px)' } : { top: 'calc(100% + 10px)' }),
                zIndex: 3,
                alignItems: 'flex-start',
                width: 'max-content',
                maxWidth: 240,
                px: 1.25,
                py: 0.75,
                borderRadius: 1.5,
                bgcolor: 'neutral.black',
                color: 'common.white',
                boxShadow: 3,
                transform: 'translateX(-50%)',
                '[dir=rtl] &': { transform: 'translateX(50%)' },
                animation: 'holidaysSpotlightBounce 1.6s ease-in-out infinite',
                '@keyframes holidaysSpotlightBounce': {
                    '0%, 100%': { marginBottom: 0, marginTop: 0 },
                    '50%': { marginBottom: pointsDown ? 6 : -6, marginTop: pointsDown ? -6 : 6 }
                },
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                // The caret, drawn as a square rotated under the bubble.
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    insetInlineStart: '50%',
                    marginInlineStart: `-${CARET_SIZE}px`,
                    ...(pointsDown ? { bottom: -CARET_SIZE + 1 } : { top: -CARET_SIZE + 1 }),
                    borderInlineStart: `${CARET_SIZE}px solid transparent`,
                    borderInlineEnd: `${CARET_SIZE}px solid transparent`,
                    ...(pointsDown
                        ? { borderTop: `${CARET_SIZE}px solid` }
                        : { borderBottom: `${CARET_SIZE}px solid` }),
                    borderTopColor: pointsDown ? 'neutral.black' : undefined,
                    borderBottomColor: pointsDown ? undefined : 'neutral.black'
                }
            }}
        >
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.4 }}>{message}</Typography>

            {/*
             * A span, not an IconButton: the bubble lives inside the tab's own <button>, and a
             * button inside a button is invalid HTML — React reports it as a hydration error.
             */}
            <Box
                component="span"
                role="button"
                tabIndex={0}
                aria-label={closeLabel}
                onClick={event => {
                    // Closing the bubble must not select the tab underneath it.
                    event.preventDefault()
                    event.stopPropagation()
                    onClose()
                }}
                onKeyDown={event => {
                    if (event.key !== 'Enter' && event.key !== ' ') return
                    event.preventDefault()
                    event.stopPropagation()
                    onClose()
                }}
                sx={{ display: 'inline-flex', cursor: 'pointer', color: 'common.white' }}
            >
                <CloseRoundedIcon sx={{ fontSize: 14 }} />
            </Box>
        </Stack>
    )
}

export { HolidaysSpotlight }
export default HolidaysSpotlight
